import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useWallet } from "../wallet/WalletContext";
import { contracts } from "../config";
import {
  cancelOrder, claimFunds, costOf, fromUnits, getBalanceUnits, getBook, getClaims, getExchangeConfig, getMarket,
  getOrdersByOwner, getRecentTrades, placeOrder, symCode, symPrec, toUnits,
  type Claim, type ExchangeConfig, type Market, type Order, type Trade,
} from "../api/exchangeApi";
import { showError, showSuccess, showWarning, confirmAction } from "../components/Dialog";

type Side = "buy" | "sell";
type Kind = "limit" | "market";

interface Level { price: bigint; base: bigint }

/** Aggregates orders into price levels. Bid `remaining` is quote escrow → convert to base. */
function levels(orders: Order[], isBid: boolean, basePrec: number): Level[] {
  const scale = 10n ** BigInt(basePrec);
  const out: Level[] = [];
  for (const o of orders) {
    const price = BigInt(o.price);
    const base = isBid ? (BigInt(o.remaining) * scale) / price : BigInt(o.remaining);
    const last = out[out.length - 1];
    if (last && last.price === price) last.base += base;
    else out.push({ price, base });
  }
  return out;
}

/** Walks the book the way the contract will (fees included) to estimate a market order. */
function simulate(side: Side, amount: bigint, asks: Order[], bids: Order[], basePrec: number, feeBps: bigint, maxFills: number) {
  const scale = 10n ** BigInt(basePrec);
  let base = 0n, quote = 0n, fee = 0n, fills = 0;
  if (side === "buy") {
    let budget = amount;
    for (const o of asks) {
      if (fills >= maxFills || budget <= 0n) break;
      const p = BigInt(o.price);
      let b = (budget * 10000n * scale) / (p * (10000n + feeBps));
      if (b > BigInt(o.remaining)) b = BigInt(o.remaining);
      while (b > 0n && costOf(b, p, basePrec) + (costOf(b, p, basePrec) * feeBps) / 10000n > budget) b--;
      if (b === 0n) break;
      const c = costOf(b, p, basePrec), f = (c * feeBps) / 10000n;
      budget -= c + f; base += b; quote += c; fee += f; fills++;
      if (b < BigInt(o.remaining)) break;
    }
  } else {
    let left = amount;
    for (const o of bids) {
      if (fills >= maxFills || left <= 0n) break;
      const p = BigInt(o.price);
      const can = (BigInt(o.remaining) * scale) / p;
      if (can === 0n) continue;
      const b = left < can ? left : can;
      const c = costOf(b, p, basePrec), f = (c * feeBps) / 10000n;
      left -= b; base += b; quote += c; fee += f; fills++;
    }
  }
  return { base, quote, fee };
}

export function ExchangeMarket() {
  const { marketId } = useParams();
  const id = Number(marketId);
  const { session, account, connect, connecting } = useWallet();

  const [m, setM] = useState<Market | null | undefined>(undefined);
  const [cfg, setCfg] = useState<ExchangeConfig | null>(null);
  const [bids, setBids] = useState<Order[]>([]);
  const [asks, setAsks] = useState<Order[]>([]);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [myBids, setMyBids] = useState<Order[]>([]);
  const [myAsks, setMyAsks] = useState<Order[]>([]);
  const [claims, setClaims] = useState<Claim[]>([]);
  const [balBase, setBalBase] = useState<bigint | null>(null);
  const [balQuote, setBalQuote] = useState<bigint | null>(null);

  const [side, setSide] = useState<Side>("buy");
  const [kind, setKind] = useState<Kind>("limit");
  const [price, setPrice] = useState("");
  const [amount, setAmount] = useState(""); // base tokens (limit / market sell) or quote to spend (market buy)
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [mk, c] = await Promise.all([getMarket(id), getExchangeConfig()]).catch(() => [null, null] as const);
    setM(mk);
    setCfg(c);
    if (!mk) return;
    const [b, a, t] = await Promise.all([getBook(id, "bids", 200), getBook(id, "asks", 200), getRecentTrades(id, 40)]);
    setBids(b);
    setAsks(a);
    setTrades(t);
    if (account) {
      const [mb, ma, cl, bb, bq] = await Promise.all([
        getOrdersByOwner(id, "bids", account),
        getOrdersByOwner(id, "asks", account),
        getClaims(account).catch(() => []),
        getBalanceUnits(account, mk.base).catch(() => 0n),
        getBalanceUnits(account, mk.quote).catch(() => 0n),
      ]);
      setMyBids(mb);
      setMyAsks(ma);
      setClaims(cl);
      setBalBase(bb);
      setBalQuote(bq);
    }
  }, [id, account]);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const bp = m ? symPrec(m.base) : 0;
  const qp = m ? symPrec(m.quote) : 0;
  const B = m ? symCode(m.base) : "";
  const Q = m ? symCode(m.quote) : "";
  const feeBps = BigInt(cfg?.taker_fee_bps ?? 0);
  const askLevels = useMemo(() => levels(asks, false, bp).slice(0, 15), [asks, bp]);
  const bidLevels = useMemo(() => levels(bids, true, bp).slice(0, 15), [bids, bp]);
  const maxLevel = [...askLevels, ...bidLevels].reduce((x, l) => (l.base > x ? l.base : x), 1n);

  // ---- derived order numbers
  let parseErr: string | null = null;
  let priceU = 0n, amountU = 0n, sendU = 0n;
  let summary = "";
  try {
    if (kind === "limit") {
      if (price) priceU = toUnits(price, qp);
      if (amount) amountU = toUnits(amount, bp);
      if (priceU > 0n && amountU > 0n) {
        const total = costOf(amountU, priceU, bp);
        sendU = side === "buy" ? total : amountU;
        summary = side === "buy"
          ? `You send ${fromUnits(total, qp, false)} ${Q}. Anything that fills right away pays the ${(Number(feeBps) / 100).toFixed(2)}% taker fee out of this, so you get slightly fewer ${B}; the rest waits on the book.`
          : `You send ${fromUnits(amountU, bp, false)} ${B} and receive up to ${fromUnits(total, qp, false)} ${Q} (minus the ${(Number(feeBps) / 100).toFixed(2)}% taker fee on anything that fills right away).`;
      }
    } else if (amount && m) {
      const prec = side === "buy" ? qp : bp;
      amountU = toUnits(amount, prec);
      sendU = amountU;
      const sim = simulate(side, amountU, asks, bids, bp, feeBps, cfg?.max_fills ?? 25);
      summary = sim.base === 0n
        ? "Nothing on the book to fill against right now."
        : side === "buy"
        ? `≈ ${fromUnits(sim.base, bp)} ${B} for ${fromUnits(sim.quote + sim.fee, qp)} ${Q} (incl. ${fromUnits(sim.fee, qp)} fee). Anything unfilled is refunded.`
        : `≈ ${fromUnits(sim.quote - sim.fee, qp)} ${Q} for ${fromUnits(sim.base, bp)} ${B} (after ${fromUnits(sim.fee, qp)} fee). Anything unfilled is refunded.`;
    }
  } catch (e) {
    parseErr = e instanceof Error ? e.message : String(e);
  }

  async function submit() {
    if (!session || !m) return;
    if (sendU <= 0n) return;
    if (kind === "limit" && side === "buy" && sendU < BigInt(m.min_quote)) {
      return showWarning(`Minimum order on this market is ${fromUnits(m.min_quote, qp)} ${Q}.`, "Order too small");
    }
    const ok = await confirmAction({
      title: `${side === "buy" ? "Buy" : "Sell"} ${B}`,
      message: summary,
      confirmLabel: side === "buy" ? "Place buy order" : "Place sell order",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await placeOrder(session, m, side, sendU, kind === "market" ? "market" : priceU, false);
      showSuccess("Order sent. Filled parts pay out right away; anything left rests on the book.", "Order placed");
      setAmount("");
      setTimeout(load, 1500);
    } catch (e) {
      showError(e, "Couldn't place the order");
    } finally {
      setBusy(false);
    }
  }

  async function cancel(o: Order, isBid: boolean) {
    if (!session) return;
    try {
      await cancelOrder(session, id, isBid, o.id);
      showSuccess("Order cancelled and refunded.", "Cancelled");
      setTimeout(load, 1500);
    } catch (e) {
      showError(e, "Couldn't cancel");
    }
  }

  async function claim(c: Claim) {
    if (!session) return;
    try {
      await claimFunds(session, c.id);
      showSuccess(`${c.amount.quantity} sent to your wallet.`, "Claimed");
      setTimeout(load, 1500);
    } catch (e) {
      showError(e, "Couldn't claim");
    }
  }

  if (m === undefined) return <div className="page"><div className="state-message">Loading market…</div></div>;
  if (m === null)
    return (
      <div className="page">
        <div className="state-message state-message--error">Market not found on <code>{contracts.orderbook}</code>.</div>
        <Link to="/exchange" className="link-button">← All markets</Link>
      </div>
    );

  const myOrders = [...myBids.map((o) => ({ o, isBid: true })), ...myAsks.map((o) => ({ o, isBid: false }))];
  const pick = (p: bigint) => { setKind("limit"); setPrice(fromUnits(p, qp)); };

  return (
    <div className="page">
      <div className="page__header">
        <Link to="/exchange" className="ex-back">← All markets</Link>
        <h1>{m.title || `${B} / ${Q}`}</h1>
        <p className="page__subtitle">
          {B}@{m.base.contract} priced in {Q}@{m.quote.contract}
          {m.website && <> · <a href={m.website} target="_blank" rel="noreferrer" className="link-button">{m.website.replace(/^https?:\/\//, "")}</a></>}
          {" · "}Last {BigInt(m.last_price) > 0n ? `${fromUnits(m.last_price, qp)} ${Q}` : "—"}
          {cfg && <> · Taker fee {(cfg.taker_fee_bps / 100).toFixed(2)}%</>}
        </p>
      </div>
      {(!m.enabled || cfg?.paused) && (
        <div className="callout callout--warning">{cfg?.paused ? "Trading is paused." : "This market is disabled."} You can still cancel your orders.</div>
      )}

      <div className="ex-layout">
        <section className="ex-panel">
          <h2 className="ex-panel__title">Order book</h2>
          <div className="ex-book">
            <div className="ex-book__head"><span>Price ({Q})</span><span>Amount ({B})</span></div>
            <div className="ex-book__side ex-book__side--asks">
              {askLevels.length === 0 && <div className="ex-book__empty">No sell orders</div>}
              {[...askLevels].reverse().map((l) => (
                <button key={`a${l.price}`} className="ex-book__row ex-book__row--ask" onClick={() => pick(l.price)}>
                  <span className="ex-book__depth" style={{ width: `${Number((l.base * 100n) / maxLevel)}%` }} />
                  <span className="ex-down">{fromUnits(l.price, qp)}</span>
                  <span>{fromUnits(l.base, bp)}</span>
                </button>
              ))}
            </div>
            <div className="ex-book__spread">
              {askLevels[0] && bidLevels[0]
                ? `Spread ${fromUnits(askLevels[0].price - bidLevels[0].price, qp)} ${Q}`
                : "—"}
            </div>
            <div className="ex-book__side">
              {bidLevels.length === 0 && <div className="ex-book__empty">No buy orders</div>}
              {bidLevels.map((l) => (
                <button key={`b${l.price}`} className="ex-book__row ex-book__row--bid" onClick={() => pick(l.price)}>
                  <span className="ex-book__depth" style={{ width: `${Number((l.base * 100n) / maxLevel)}%` }} />
                  <span className="ex-up">{fromUnits(l.price, qp)}</span>
                  <span>{fromUnits(l.base, bp)}</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="ex-panel">
          <div className="ex-tabs">
            <button className={`ex-tab ex-tab--buy ${side === "buy" ? "ex-tab--active" : ""}`} onClick={() => setSide("buy")}>Buy {B}</button>
            <button className={`ex-tab ex-tab--sell ${side === "sell" ? "ex-tab--active" : ""}`} onClick={() => setSide("sell")}>Sell {B}</button>
          </div>
          <div className="ex-kind">
            <label className="ex-radio"><input type="radio" checked={kind === "limit"} onChange={() => setKind("limit")} /> Limit</label>
            <label className="ex-radio"><input type="radio" checked={kind === "market"} onChange={() => setKind("market")} /> Market</label>
          </div>
          <div className="ex-form">
            {kind === "limit" && (
              <label>
                Price ({Q} per {B})
                <input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0.0" />
              </label>
            )}
            <label>
              {kind === "market" && side === "buy" ? `Spend (${Q})` : `Amount (${B})`}
              <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.0" />
            </label>
            {account && (
              <div className="ex-balances">
                Balance: {balQuote !== null ? fromUnits(balQuote, qp) : "…"} {Q} · {balBase !== null ? fromUnits(balBase, bp) : "…"} {B}
              </div>
            )}
            {parseErr && <div className="state-message state-message--error">{parseErr}</div>}
            {summary && !parseErr && <div className="ex-summary">{summary}</div>}
            {!session ? (
              <button className="btn btn--primary" onClick={connect} disabled={connecting}>{connecting ? "Connecting…" : "Connect wallet to trade"}</button>
            ) : (
              <button
                className={`btn ${side === "buy" ? "btn--primary" : "btn--danger"}`}
                onClick={submit}
                disabled={busy || sendU <= 0n || !!parseErr || !m.enabled || !!cfg?.paused}
              >
                {busy ? "Waiting for wallet…" : `${side === "buy" ? "Buy" : "Sell"} ${B}`}
              </button>
            )}
            <p className="ex-fineprint">
              Minimum resting order {fromUnits(m.min_quote, qp)} {Q}. Orders are a token transfer to{" "}
              <code>{contracts.orderbook}</code>; cancel any time to get the unfilled part back.
            </p>
          </div>
        </section>
      </div>

      {account && (myOrders.length > 0 || claims.length > 0) && (
        <section className="ex-panel ex-panel--wide">
          <h2 className="ex-panel__title">Your open orders</h2>
          {claims.length > 0 && (
            <div className="callout callout--warning">
              You have funds waiting to be claimed:{" "}
              {claims.map((c) => (
                <button key={c.id} className="btn btn--small btn--outline" onClick={() => claim(c)}>Claim {c.amount.quantity}</button>
              ))}
            </div>
          )}
          {myOrders.length > 0 && (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead><tr><th>Side</th><th>Price ({Q})</th><th>Remaining</th><th>Placed</th><th /></tr></thead>
                <tbody>
                  {myOrders.map(({ o, isBid }) => (
                    <tr key={`${isBid}-${o.id}`}>
                      <td className={isBid ? "ex-up" : "ex-down"}>{isBid ? "Buy" : "Sell"}</td>
                      <td>{fromUnits(o.price, qp)}</td>
                      <td>{isBid ? `${fromUnits(o.remaining, qp)} ${Q}` : `${fromUnits(o.remaining, bp)} ${B}`}</td>
                      <td>{new Date(o.placed + "Z").toLocaleString()}</td>
                      <td><button className="btn btn--small btn--outline" onClick={() => cancel(o, isBid)}>Cancel</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <section className="ex-panel ex-panel--wide">
        <h2 className="ex-panel__title">Recent trades</h2>
        {trades.length === 0 ? (
          <div className="state-message">No trades yet.</div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead><tr><th>Time</th><th>Price ({Q})</th><th>Amount ({B})</th><th>Total ({Q})</th><th>Taker</th></tr></thead>
              <tbody>
                {trades.map((t) => (
                  <tr key={t.id}>
                    <td>{new Date(t.time + "Z").toLocaleString()}</td>
                    <td className={Number(t.taker_side) === 0 ? "ex-up" : "ex-down"}>{fromUnits(t.price, qp)}</td>
                    <td>{fromUnits(t.base_amount, bp)}</td>
                    <td>{fromUnits(t.quote_amount, qp)}</td>
                    <td>{t.taker} {Number(t.taker_side) === 0 ? "bought" : "sold"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
